import { TAppDispatch, TGetState, TRootState } from 'store/types'
import { getAiVideoModels_API, getAIVideoProjects_API } from 'api'
import {
  AIVideoSocketMessageStatus,
  IAIVideoGenerationParams,
  IAIVideoGenerationRequestBody,
  IAIVideoListParams,
  TReferenceMode
} from 'types/aiVideo'
import slice from 'store/features/aiVideo/slice'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import {
  buildAIVideoGenerationRequest,
  checkAndShiftMediaItems,
  getDefaultReferenceMode,
  findMatchingOption,
  getBestFreeQualityOption,
  getFirstFrameUrl,
  getDefaultAIVideoModel,
  getMentionSentDisplayNumbers
} from '../utils'
import { resolveMentionsForRequest } from 'utils/promptMentions'
import { ReferenceMode } from 'containers/SubSidebar/AIVideoContent/consts'
import { ErrorKeys } from 'consts/errorKeys'
import { captureException } from '@sentry/react'
import { ApiError, ReduxThunkError } from 'utils/errors'
import { projectSelectors } from 'store/features/project/selectors'
import { formThunks } from 'store/features/aiVideo/thunks/form'
import { animateThunks } from 'store/features/aiVideo/thunks/animate'
import { smartGenerationThunks } from 'store/features/aiVideo/thunks/generation'
import { uploadThunks } from 'store/features/aiVideo/thunks/upload'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { handleApiError } from 'store/utils'
import { isSmartEditModeSelector } from 'store/selectors'
import { sortArraysByKey } from 'store/features/projectData/utils'
import { getVerifiedModel } from './utils'
import { versionHistoryThunks } from './versionHistory'
import {
  getAIVideoUserPreferences,
  saveAIVideoUserPreferences
} from 'utils/localStorage'
import { userSelectors } from 'store/features/user/selectors'
import { MIN_PREMIUM_QUALITY } from 'consts'

const { actions } = slice

interface IUpdateSelectedModelParams {
  modelName: string
  duration?: number
  quality?: number
  aspectRatio?: string | null
}
const updateSelectedModel =
  ({ modelName, duration, quality, aspectRatio }: IUpdateSelectedModelParams) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const model = getVerifiedModel(modelName, state.aiVideo.aiVideoModels)
    const multiRefImagesBucket = aiVideoSelectors.multiRefImagesBucket(state)
    const framesBucket = aiVideoSelectors.framesBucket(state)
    const hasOnlyFramesBucket =
      framesBucket.length > 0 && !multiRefImagesBucket.length

    const { duration: defaultDuration, quality: defaultQuality } =
      model.defaultValues

    // Fall back to model defaults when neither an explicit value nor a prior
    // user selection is available — the initial state has 0/0, and feeding 0
    // into closest-value matching would resolve to the smallest option.
    const selectedQuality =
      quality || aiVideoSelectors.quality(state) || defaultQuality
    const selectedDuration =
      duration || aiVideoSelectors.duration(state) || defaultDuration

    dispatch(actions.setSelectedModel(model.model))
    // Find matching options or fallback to defaults (first item)
    const matchingDurationOption = findMatchingOption({
      items: model.options,
      key: 'duration',
      selectedValue: selectedDuration,
      defaultValue: defaultDuration
    })

    const matchingQualityOption = findMatchingOption({
      items: matchingDurationOption.qualities,
      key: 'quality',
      selectedValue: selectedQuality,
      defaultValue: defaultQuality
    })

    const isFramesActiveAndFilled =
      aiVideoSelectors.isFramesActiveAndFilled(state)
    const currentAspectRatio = aiVideoSelectors.aspectRatio(state)
    const hasUserSelectedAspectRatio =
      state.aiVideo.hasUserSelectedImageAspectRatio
    const resolvedAspectRatio =
      hasUserSelectedAspectRatio && currentAspectRatio
        ? currentAspectRatio
        : aspectRatio !== undefined
          ? aspectRatio
          : currentAspectRatio

    const isMediaResizingSupported =
      model.capabilities.isMediaResizingSupported === true

    if (
      aspectRatio !== undefined ||
      !isFramesActiveAndFilled ||
      resolvedAspectRatio ||
      isMediaResizingSupported
    ) {
      const projectAspectRatio =
        projectDataSelectors.defaultDimensions(state)?.aspectRatioString
      const availableRatios = model.capabilities.aspectRatios
      if (availableRatios?.length) {
        const matchingAspectRatio =
          availableRatios.find(ratio => ratio === resolvedAspectRatio) ||
          availableRatios.find(ratio => ratio === projectAspectRatio) ||
          availableRatios[0]
        dispatch(actions.setAspectRatio(matchingAspectRatio))
      }
    }

    // Clamp quality for free users to below premium threshold
    const isFreeUser = userSelectors.isFreeUser(state)
    const finalQualityOption =
      isFreeUser && matchingQualityOption.quality >= MIN_PREMIUM_QUALITY
        ? (getBestFreeQualityOption(matchingDurationOption.qualities) ??
          matchingQualityOption)
        : matchingQualityOption

    // Update store with matched or default values
    dispatch(actions.setDuration(matchingDurationOption.duration))
    dispatch(actions.setQuality(finalQualityOption.quality))
    dispatch(actions.setVideoGenerationCost(finalQualityOption.credit))
    dispatch(
      actions.setReferenceMode(
        getDefaultReferenceMode(model, hasOnlyFramesBucket)
      )
    )
    dispatch(actions.applyDefaultImageAspectRatio())
  }

const getPreparedRequestPayload =
  (projectId?: number) =>
  (
    _dispatch: TAppDispatch,
    getState: TGetState
  ): IAIVideoGenerationRequestBody => {
    const state = getState()
    const isConsistencyEnabled = aiVideoSelectors.isConsistencyEnabled(state)
    const isSmartEditMode = isSmartEditModeSelector(state)
    const projectMeta = projectSelectors.meta(state)
    const genAiProjectId = projectMeta?.genAiProjectId
    const refImageSelectedStyleId =
      aiVideoSelectors.refImageSelectedStyleId(state)

    const selectedModel = aiVideoSelectors.selectedModel(state)
    const isFramesActiveAndFilled =
      aiVideoSelectors.isFramesActiveAndFilled(state)
    const currentAspectRatio = aiVideoSelectors.aspectRatio(state)

    const aspectRatioForRequest = isFramesActiveAndFilled
      ? (currentAspectRatio ?? undefined)
      : currentAspectRatio || '16:9'

    const targetMediaItemId = aiVideoSelectors.targetMediaItemId(state)

    // Resolve the prompt's `@Image N` chips into the wording providers
    // understand (`reference image N`), numbered by position in the reference
    // array actually sent. This is the ONE resolve site for the mention editor —
    // generate, regenerate and consistency all build their body here, so they
    // cannot disagree. Tokens with no matching sent image are stripped.
    //
    // Smart Edit is deliberately left alone: `smartPrompt` comes from a plain
    // textarea with no mention UI, so it can only contain hand-typed `@Image N`
    // text — which resolution would rewrite or silently delete.
    const promptForRequest = isSmartEditMode
      ? aiVideoSelectors.smartPrompt(state)
      : resolveMentionsForRequest({
          text: aiVideoSelectors.prompt(state) ?? '',
          sentDisplayNumbers: getMentionSentDisplayNumbers(
            aiVideoSelectors.generationParams(state).referenceMedias,
            aiVideoSelectors.referenceMode(state)
          )
        })

    const parentProjectId =
      aiVideoSelectors.generationParams(state).parentProjectId

    // Persist generator preferences on generate only (every generation path
    // flows through here), so the user's last *generated* config — model, raw
    // Power Mode intent, quality — is restored on their next visit.
    saveAIVideoUserPreferences(userSelectors.userId(state), {
      model: selectedModel?.model ?? null,
      powerMode: aiVideoSelectors.isFastTrack(state),
      quality: aiVideoSelectors.quality(state)
    })

    return buildAIVideoGenerationRequest({
      id: isConsistencyEnabled || isSmartEditMode ? genAiProjectId : undefined,
      referenceMedias: aiVideoSelectors.apiReferenceMedias(state),
      prompt: promptForRequest,
      videoDurationSeconds: aiVideoSelectors.duration(state) || 0,
      videoModel: selectedModel?.model || '',
      aspectRatio: aspectRatioForRequest,
      quality: aiVideoSelectors.quality(state),
      aiImageId: aiVideoSelectors.aiImageId(state) || null,
      projectId,
      styleId: isSmartEditMode ? undefined : refImageSelectedStyleId,
      timelineItemId: targetMediaItemId,
      parentProjectId: isConsistencyEnabled ? undefined : parentProjectId,
      // isFastTrackActive already gates on supported && enabled && !isFreeUser
      isFastTrack: aiVideoSelectors.isFastTrackActive(state)
    })
  }

// Async thunks

const checkAndShiftMediaItemsForRegeneration =
  (selectedItemId: string, newDuration: number) =>
  (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const mediaItems = projectDataSelectors.mediaItems(state)
    const sortedMediaItems = sortArraysByKey('startTime', mediaItems)

    const selectedItemIndex = sortedMediaItems.findIndex(
      item => item.id === selectedItemId
    )

    if (selectedItemIndex === -1) {
      return { success: false, error: 'Selected item index not found' }
    }

    const selectedItem = sortedMediaItems[selectedItemIndex]
    const newItemEndTime = selectedItem.startTime + newDuration

    // Check space availability and shift items if needed
    const shiftResult = checkAndShiftMediaItems({
      mediaItems: sortedMediaItems,
      selectedItemIndex,
      newItemEndTime
    })

    if (shiftResult.error) {
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))

      return { success: false, error: shiftResult.error }
    }

    return { success: true, shifted: false }
  }

const getAIVideoHistory =
  (params: IAIVideoListParams = {}) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const { aiVideoHistory } = state.aiVideo
    const currentProjectId = projectSelectors.id(state)

    const isScopedWithoutProject =
      aiVideoHistory.isScopedToCurrentProject && !currentProjectId

    if (isScopedWithoutProject) {
      return
    }

    try {
      dispatch(actions.setAiVideoHistoryLoading(true))

      const response = await getAIVideoProjects_API({
        ...params,
        videoProjectId: aiVideoHistory.isScopedToCurrentProject
          ? Number(currentProjectId)
          : undefined
      })

      const successfulProjects = response.data.filter(
        item => item.status !== AIVideoSocketMessageStatus.FAILED
      )

      // update pagination metadata
      dispatch(actions.setAiVideoHistoryTotalCount(response.totalCount!))
      dispatch(
        actions.setAiVideoHistoryRawOffset(params.offset! + params.limit!)
      )

      const isInitialFetch = params.offset === 0
      const currentHistory = aiVideoSelectors.historyList(state) ?? []

      if (isInitialFetch) {
        // Preserve locally-added processing videos not yet in API response
        const pendingGeneratingVideos = currentHistory.filter(
          item =>
            item.status === AIVideoSocketMessageStatus.PROCESSING &&
            !successfulProjects.some(project => project.id === item.id)
        )
        dispatch(
          actions.setAiVideoHistory([
            ...pendingGeneratingVideos,
            ...successfulProjects
          ])
        )
      } else {
        // Append paginated results to existing history
        dispatch(
          actions.setAiVideoHistory([...currentHistory, ...successfulProjects])
        )
      }
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      dispatch(actions.setAiVideoHistoryLoading(false))
    }
  }

const getAIVideoModels =
  () => async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const existingModels = aiVideoSelectors.aiVideoModels(state)
    const isLoadingTabData = aiVideoSelectors.isLoadingTabData(state)

    if (existingModels.length > 0 || isLoadingTabData) {
      return
    }

    try {
      dispatch(actions.setIsLoadingTabData(true))
      const response = await getAiVideoModels_API()
      const fetchedModels = response.data
      const aiFlowVideoModel = projectSelectors.aiFlowVideoModelCode(state)

      dispatch(actions.setAIVideoModels(fetchedModels))

      if (fetchedModels.length === 0) {
        return
      }

      // Determine which model to preselect: project meta > URL query > stored
      // user preference > Renderforest Fast as the catalog default.
      const urlSearchParams = new URLSearchParams(window.location.search)
      const modelCodeFromQuery = urlSearchParams.get('aiVideoModel')

      const modelFromQuery = modelCodeFromQuery
        ? fetchedModels.find(model => model.model === modelCodeFromQuery)?.model
        : null

      const userPreferences = getAIVideoUserPreferences(
        userSelectors.userId(state)
      )

      // When the panel opens to regenerate/edit/recreate an existing item, its
      // form is already populated from that item (see `setRegenerationForm` /
      // `setVideoGenerationForm`). Don't override it with the stored
      // "new generation" preferences (model restore + Power Mode default).
      // Detected by the existing-item markers — targetMediaItemId (regenerate)
      // and parentProjectId (recreate/regenerate) — not by the selected model.
      const latestState = getState()
      const latestGenerationParams =
        aiVideoSelectors.generationParams(latestState)
      const isExistingItemForm =
        latestGenerationParams.targetMediaItemId !== null ||
        latestGenerationParams.parentProjectId !== undefined

      // Restore the user's last generated model when nothing more specific
      // (project meta / URL) is requested and it still exists in the catalog.
      const storedModel =
        userPreferences.model &&
        fetchedModels.some(model => model.model === userPreferences.model)
          ? userPreferences.model
          : null
      const defaultModel = getDefaultAIVideoModel(fetchedModels).model
      const selectedModel = isExistingItemForm
        ? null
        : (aiFlowVideoModel ?? modelFromQuery ?? storedModel ?? defaultModel)

      // Power Mode default once the user tier is known: off for free users, on
      // for paid; a previously saved choice wins. Skipped for an existing-item
      // form so the item's own setting is preserved.
      if (!isExistingItemForm) {
        dispatch(
          actions.setIsFastTrack(
            userPreferences.powerMode ?? !userSelectors.isFreeUser(state)
          )
        )
      }

      if (selectedModel) {
        const defaultQuality = fetchedModels.find(
          model => model.model === selectedModel
        )?.defaultValues.quality

        dispatch(
          updateSelectedModel({
            modelName: selectedModel,
            quality: userPreferences.quality ?? defaultQuality
          })
        )
      }
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      dispatch(actions.setIsLoadingTabData(false))
      dispatch(actions.setIsModelsFetched(true))
    }
  }

const saveCurrentSettingsAsDefault =
  () => (_dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const { selectedModel, duration, aspectRatio, quality } =
      state.aiVideo.generationParams
    _dispatch(
      actions.setDefaultGenerationSettings({
        selectedModel,
        duration,
        aspectRatio,
        quality
      })
    )
  }

const updateModelCreditsPerDuration =
  (params: {
    modelCode: string | null
    duration?: number
    quality?: number | null
  }) =>
  (dispatch: TAppDispatch, getState: TGetState) => {
    const { modelCode, duration, quality } = params
    if (!modelCode) {
      return
    }

    const state = getState()
    const model = state.aiVideo.aiVideoModels.find(m => m.model === modelCode)

    if (!model) {
      return
    }

    const { duration: defaultDuration, quality: defaultQuality } =
      model.defaultValues

    // Use provided values or fallback to defaults (first item)
    const targetDuration = duration || defaultDuration
    const targetQuality = quality || defaultQuality

    const matchingDurationOption = findMatchingOption({
      items: model.options,
      key: 'duration',
      selectedValue: targetDuration,
      defaultValue: defaultDuration
    })

    const matchingQualityOption = findMatchingOption({
      items: matchingDurationOption.qualities,
      key: 'quality',
      selectedValue: targetQuality,
      defaultValue: defaultQuality
    })

    // Clamp quality for free users to below premium threshold
    const isFreeUser = userSelectors.isFreeUser(state)
    const bestFree =
      isFreeUser && targetQuality >= MIN_PREMIUM_QUALITY
        ? getBestFreeQualityOption(matchingDurationOption.qualities)
        : null
    const finalQuality = bestFree?.quality ?? targetQuality
    const finalCredit = bestFree?.credit ?? matchingQualityOption.credit

    if (!finalCredit) {
      return
    }

    // Update store with resolved values
    dispatch(actions.setSelectedModel(modelCode))
    dispatch(actions.setDuration(targetDuration))
    dispatch(actions.setQuality(finalQuality))
    dispatch(actions.setVideoGenerationCost(finalCredit))
  }

const setGenerationParams =
  (params: IAIVideoGenerationParams) => (dispatch: TAppDispatch) => {
    dispatch(actions.setGenerationParams(params))
    dispatch(
      updateModelCreditsPerDuration({
        modelCode: params.selectedModel,
        duration: params.duration,
        quality: params.quality
      })
    )
  }

const resolveAspectRatioWithNoFrameReference = (
  state: TRootState
): string | null => {
  const ai = state.aiVideo
  const model = ai.aiVideoModels.find(
    m => m.model === ai.generationParams.selectedModel
  )
  const availableRatios = model?.capabilities.aspectRatios
  if (!availableRatios?.length) {
    return ai.generationParams.aspectRatio
  }

  const projectAspectRatio =
    projectDataSelectors.defaultDimensions(state)?.aspectRatioString
  const fromProject = projectAspectRatio
    ? availableRatios.find(ratio => ratio === projectAspectRatio)
    : undefined

  return fromProject ?? availableRatios[0] ?? null
}

const removeReferenceMediaItem =
  (payload: { id: string; bucket: TReferenceMode }) =>
  (dispatch: TAppDispatch, getState: TGetState) => {
    dispatch(actions.removeReferenceMediaItem(payload))
    if (payload.bucket !== ReferenceMode.FRAMES) return
    const state = getState()
    if (state.aiVideo.hasUserSelectedImageAspectRatio) return
    if (getFirstFrameUrl(state.aiVideo.generationParams.referenceMedias)) return

    const next = resolveAspectRatioWithNoFrameReference(state)
    dispatch(actions.setAspectRatio(next))
  }

export const aiVideoThunks = {
  ...actions,
  ...formThunks,
  ...animateThunks,
  ...smartGenerationThunks,
  ...uploadThunks,
  ...versionHistoryThunks,
  removeReferenceMediaItem,
  setGenerationParams,
  getPreparedRequestPayload,
  checkAndShiftMediaItemsForRegeneration,
  getAIVideoHistory,
  getAIVideoModels,
  updateSelectedModel,
  updateModelCreditsPerDuration,
  saveCurrentSettingsAsDefault
}
